import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checklist,
  Drawer,
  Field,
  Input,
  PowerSelect,
  Select
} from '@itds/elements/index.js';
import { NOTIFICATION_TRIGGERS } from '@itds/Constants';

import { Api } from '@brokerage/utils/Api';

const Edit = ({ agencyId, onClose, onSave, products, show, trigger }) => {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    const new_trigger = trigger || {};

    setData({
      id: new_trigger.id,
      agency_id: agencyId,
      emails: new_trigger.emails || [],
      groups: new_trigger.groups || [],
      name: new_trigger.name || '',
      notification_type: new_trigger.notification_type || 'email',
      product_id: new_trigger.product_id || '',
      product_type: new_trigger.product_type || '',
      trigger: new_trigger.trigger || '',
      web_call_url: new_trigger.web_call_url || ''
    });
  }, [trigger]);

  const handleInputChange = (name, e) => {
    setData({ ...data, [name]: e.target.value });
  };

  const handleProductChange = e => {
    if (e.target.value === 'all') {
      setData({ ...data, product_id: e.target.value, product_type: 'all' });
    } else {
      const product = products.find(p => p.id === parseInt(e.target.value, 10));

      setData({ ...data, product_id: e.target.value, product_type: product.type });
    }
  };

  const handleGroupsChange = selected => {
    setData({ ...data, groups: selected });
  };

  const handlePowerSelectChange = selected => {
    setData({
      ...data,
      emails: selected.map(s => s.value)
    });
  };

  const handleSave = () => {
    const errors = [];

    if (data.notification_type === 'email') {
      if (!data.groups.length && !data.emails.length) {
        errors.push('recipient');
      }
    }

    if (data.notification_type === 'web_call') {
      if (!data.web_call_url.length) {
        errors.push('web_call_url');
      }
    }

    if (!data.name) {
      errors.push('name');
    }

    if (!data.trigger) {
      errors.push('trigger');
    }

    if (!data.notification_type) {
      errors.push('notification_type');
    }

    if (data.notification_type === 'web_call' && !data.web_call_url) {
      errors.push('web_call_url');
    }

    setErrors(errors);

    if (!errors.length) {
      const { id, ...payload } = data;

      window.scrollTo(0, 0);

      Api.saveAgencyNotificationTrigger(id, payload).then(() => {
        onSave();
      });
    }
  };

  return (
    <Drawer
      alignRight={true}
      footer={<Button onClick={handleSave.bind(null)}>Save Changes</Button>}
      onClose={onClose}
      show={show}
      title={data.id ? 'Edit Notification Trigger' : 'New Notification Trigger'}
    >
      <Field description='This will be the email subject' label='Name'>
        <Input
          invalid={errors.includes('name')}
          onChange={handleInputChange.bind(null, 'name')}
          required={true}
          value={data.name}
        />
      </Field>

      <Field label='Event'>
        <Select
          invalid={errors.includes('trigger')}
          onChange={handleInputChange.bind(null, 'trigger')}
          options={NOTIFICATION_TRIGGERS.map(nt => ({ value: nt.value, label: nt.label }))}
          placeholder='-- Select One --'
          required={true}
          value={data.trigger}
        />
      </Field>

      <Field label='Delivery Method'>
        <Select
          invalid={errors.includes('notification_type')}
          onChange={handleInputChange.bind(null, 'notification_type')}
          options={[
            { value: 'email', label: 'Email' },
            { value: 'web_call', label: 'Web Call' }
          ]}
          placeholder='-- Select One --'
          required={true}
          value={data.notification_type}
        />
      </Field>

      <Field label='Product'>
        <Select
          onChange={handleProductChange.bind(null)}
          options={[{ value: 'all', label: 'All Products' }].concat(
            products.map(p => ({ value: p.id, label: `${p.name || p.id} (${p.type})` }))
          )}
          value={data.product_id}
        />
      </Field>

      {data.notification_type === 'web_call' ? (
        <Field label='Web Call URL'>
          <Input
            invalid={errors.includes('web_call_url')}
            onChange={handleInputChange.bind(null, 'web_call_url')}
            value={data.web_call_url}
          />
        </Field>
      ) : null}

      {data.notification_type === 'email' ? (
        <>
          <Field invalid={errors.includes('recipient')} label='Groups'>
            <Checklist
              onChange={handleGroupsChange}
              options={[
                { label: 'Sales Reps', value: 'sales_reps' },
                { label: 'Admins', value: 'admins' }
              ]}
              selected={data.groups || []}
            />
          </Field>
          <Field label='Emails'>
            <PowerSelect
              creatable={true}
              invalid={errors.includes('recipient')}
              isMulti={true}
              newOptionCheck={inputValue => {
                const REGEX_EMAIL =
                  "([a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@" +
                  '(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)';

                return new RegExp('^' + REGEX_EMAIL + '$', 'i').test(inputValue);
              }}
              noOptionsMessage='Invalid email, keep typing...'
              onChange={handlePowerSelectChange}
              placeholder='Add some emails...'
              value={data && data.emails ? data.emails.map(e => ({ value: e, label: e })) : []}
            />
          </Field>
        </>
      ) : null}
    </Drawer>
  );
};

Edit.propTypes = {
  agencyId: PropTypes.node,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  products: PropTypes.array,
  show: PropTypes.bool,
  trigger: PropTypes.object
};

export default Edit;
